import { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import { AdminContext } from "react-admin";
import { createTheme } from "@material-ui/core";
import ReactGA from "react-ga4";
import usePageTracking from "./hooks/usePageTracking";

import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
import polyglotI18nProvider from "ra-i18n-polyglot";
import englishMessages from "ra-language-english";

import SignInPage from "../src/pages/auth/SignIn";
import DashboardPage from "./pages/Dashboard";
import ForgotPassword from "../src/pages/auth/ForgotPassword";
import ResetPassword from "./pages/auth/ResetPassword";
import NotificationsPage from "./pages/Notifications";
import firebaseDataProvider from "./firebaseDataProvider";
import ProfilePage from "./pages/Profile";
import UsersPage from "./pages/Users";
import UsersEdit from "./components/users/UsersEdit";
import UsersCreate from "./components/users/UsersCreate";
import Topup from "./pages/Mro.js/Topup";
import EmployeeTypes from "./pages/Mro.js/EmployeeTypes";
import EmployeeTypeCreate from "./components/EmployeeType/EmployeeTypeCreate";
import EmployeeTypeEdit from "./components/EmployeeType/EmployeeTypeEdit";
import Employee from "./pages/Mro.js/Employee";
import EmployeeCreate from "./components/Employee/EmployeeCreate";
import EmployeeEdit from "./components/Employee/EmployeeEdit";
import VendingMachines from "./pages/VendingMachines";
import MachineEdit from "./components/Machines/MachineEdit";
import SlotEdit from "./components/Machines/SlotEdit";
import SlotItemBoardEdit from "./components/Machines/SlotItemBoardEdit";
import ProductList from "./components/Products/ProductList";
import ProductEdit from "./components/Products/ProductEdit";
import ProductCreate from "./components/Products/ProductCreate";
import CategoryList from "./components/Category/CategoryList";
import CategoryEdit from "./components/Category/CategoryEdit";
import CategoryCreate from "./components/Category/CategoryCreate";
import TransactionList from "./components/Transactions/TransactionList";
import TransactionProductList from "./components/Transactions/TransactionProductList";
import TransactionEmployeeList from "./components/Transactions/TransactionEmployeeList";
import SalesTransactionList from "./components/Transactions/Sales/SalesTransactionList";
import RouteTitleSetter from "./components/RouteTitleSetter";
import { AuthProvider } from "./auth/AuthProvider";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { ACCESS } from "./components/constant";

ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);

const customTheme = createTheme({
  palette: {
    mode: "light",
  },
});

const customMsg = {
  ra: {
    navigation: {
      page_rows_per_page: "Rows per page", // Custom label for "Rows per page"
      page_range_info: "%{offsetBegin}-%{offsetEnd} of %{total}", // Custom range info
    },
  },
};

const messages = {
  en: {
    ...englishMessages,
    ...customMsg,
    ra: {
      ...englishMessages.ra,
      page: {
        list: "List",
      },
      action: {
        clear_input_value: "Clear input value",
        unselect: "Unselect",
        sort: "Sort",
        bulk_actions: "Bulk Actions", // overriding bulk actions
        edit: "Edit",
        delete: "Delete",
      },
    },
  },
};

// Create the i18nProvider with the merged messages
const i18nProvider = polyglotI18nProvider((locale) => messages[locale], "en");

function PageViewTracker() {
  const location = useLocation();

  useEffect(() => {
    const pagePath = location.pathname + location.search;
    const pageTitle = document.title;

    ReactGA.send({
      hitType: "pageview",
      page: pagePath,
      title: pageTitle,
    });
  }, [location]);

  return null;
}

const protectedRoutes = [
  { path: "/profile", element: <ProfilePage /> },

  { flag: ACCESS.USER, path: "/users", element: <UsersPage /> },
  { flag: ACCESS.USER, path: "/users/create", element: <UsersCreate /> },
  { flag: ACCESS.USER, path: "/users/edit/:id", element: <UsersEdit /> },

  { flag: ACCESS.MRO, path: "/employeeTopup", element: <Topup /> },
  { flag: ACCESS.MRO, path: "/employeeTypes", element: <EmployeeTypes /> },
  {
    flag: ACCESS.MRO,
    path: "/employeeTypes/create",
    element: <EmployeeTypeCreate />,
  },
  {
    flag: ACCESS.MRO,
    path: "/employeeTypes/edit/:id",
    element: <EmployeeTypeEdit />,
  },
  { flag: ACCESS.MRO, path: "/employees", element: <Employee /> },
  { flag: ACCESS.MRO, path: "/employees/create", element: <EmployeeCreate /> },
  { flag: ACCESS.MRO, path: "/employees/edit/:id", element: <EmployeeEdit /> },

  {
    flag: ACCESS.VENDING_MACHINE,
    path: "/machines",
    element: <VendingMachines />,
  },
  {
    flag: ACCESS.VENDING_MACHINE,
    path: "/machines/edit/:id",
    element: <MachineEdit />,
  },
  {
    flag: ACCESS.VENDING_MACHINE,
    path: "/machines/:machineId/slots/:slotNumber",
    element: <SlotEdit />,
  },
  {
    flag: ACCESS.VENDING_MACHINE,
    path: "/machines/:machineId/boards/:boardId/slots/:slotId",
    element: <SlotItemBoardEdit />,
  },

  { flag: ACCESS.INVENTORY, path: "/products", element: <ProductList /> },
  {
    flag: ACCESS.INVENTORY,
    path: "/products/create",
    element: <ProductCreate />,
  },
  {
    flag: ACCESS.INVENTORY,
    path: "/products/edit/:id",
    element: <ProductEdit />,
  },
  { flag: ACCESS.INVENTORY, path: "/categories", element: <CategoryList /> },
  {
    flag: ACCESS.INVENTORY,
    path: "/categories/create",
    element: <CategoryCreate />,
  },
  {
    flag: ACCESS.INVENTORY,
    path: "/categories/edit/:id",
    element: <CategoryEdit />,
  },

  {
    flag: ACCESS.MRO_REPORT,
    path: "/mro-transactions",
    element: <TransactionList />,
  },
  {
    flag: ACCESS.MRO_REPORT,
    path: "/mro-transactions-by-product",
    element: <TransactionProductList />,
  },
  {
    flag: ACCESS.MRO_REPORT,
    path: "/mro-transactions-by-employee",
    element: <TransactionEmployeeList />,
  },

  {
    flag: ACCESS.REPORT,
    path: "/transactions",
    element: <SalesTransactionList />,
  },

  {
    flag: ACCESS.NOTIFICATIONS,
    path: "/notifications",
    element: <NotificationsPage />,
  },
];

const AdminApp = () => {
  usePageTracking();

  return (
    <>
      <RouteTitleSetter />
      <AdminContext
        dataProvider={firebaseDataProvider}
        theme={customTheme}
        i18nProvider={i18nProvider}
      >
        <Routes>
          {protectedRoutes.map(({ path, flag, element }) => (
            <Route
              key={path}
              path={path}
              element={<ProtectedRoute flag={flag}>{element}</ProtectedRoute>}
            />
          ))}
        </Routes>
      </AdminContext>
    </>
  );
};

const App = () => {
  return (
    <>
      <Router>
        <AuthProvider>
          <RouteTitleSetter />
          <PageViewTracker />
          <Routes>
            <Route path="/" element={<SignInPage />} />
            <Route path="/ForgotPassword" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute flag={ACCESS.DASHBOARD} allowIfNoPermission>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            {/* <Route
              path="/notifications"
              element={
                <ProtectedRoute flag={ACCESS.NOTIFICATIONS}>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            /> */}

            <Route path="/*" element={<AdminApp />} />
          </Routes>
        </AuthProvider>
      </Router>
    </>
  );
};

export default App;
